import { CreateCenaRequest, GetCenaResponse } from '@shared/models'
import { useCenaContext } from './Cena.context'

const interactionKeyMap: Record<string, string> = {
  curtida: 'likes',
  comentario: 'comments',
  compartilhamento: 'shares',
  salvo: 'saveds',
}

type CenaData = ReturnType<ReturnType<typeof useCenaContext>['getAllData']>

export const mapContextToRequest = (data: CenaData, companyID?: string): CreateCenaRequest => {
  const { rede, imprensa, favorabilidade } = data

  return {
    companyID,
    social: {
      dataSourceID: 1,
      data: {
        interactionsWeight: Number(rede.interactionsWeight),
        likesP95: Number(rede.likesP95),
        likesP95P99: Number(rede.likesP95P99),
        likesP99: Number(rede.likesP99),
        commentsP95: Number(rede.commentsP95),
        commentsP95P99: Number(rede.commentsP95P99),
        commentsP99: Number(rede.commentsP99),
        approachMultiplier: Number(rede.approachMultiplier),
        channelMultiplier: Number(rede.channelMultiplier),
        complementaryMultiplier: Number(rede.complementaryMultiplier),
        keyMessage: Number(rede.keyMessage),
        socialApproachData: Object.fromEntries(rede.networkApproachData.map((i) => [i.id, Number(i.value)])) as any,
        socialVehicleData: Object.fromEntries(rede.networkVehicleData.map((i) => [i.id, Number(i.value)])) as any,
        ultraPerformance: {
          filterLikesMeta: Number(rede.filterLikesFB),
          filterCommentsMeta: Number(rede.filterCommentsFB),
          filterSharesMeta: Number(rede.filterSharesFB),
          filterLikesYoutube: Number(rede.filterLikesYT),
          filterCommentsYoutube: Number(rede.filterCommentsYT),
          filterLikesTiktok: Number(rede.filterLikesTT),
          filterCommentsTiktok: Number(rede.filterCommentsTT),
          filterSharesTiktok: Number(rede.filterSharesTT),
          extraPointP95: Number(rede.extraPointP95),
          multipliers: Object.fromEntries(
            rede.ultraPerformanceData.map((net) => [
              net.id,
              Object.fromEntries(
                net.interactions.map((inter) => [interactionKeyMap[inter.id] || inter.id, Number(inter.value)]),
              ),
            ]),
          ) as any,
        },
      },
    },
    press: {
      dataSourceID: 2,
      data: {
        formatWeight: Number(imprensa.formatWeight),
        approachMultiplier: Number(imprensa.approachMultiplier),
        vehicleMultiplier: Number(imprensa.vehicleMultiplier),
        complementaryMultiplier: Number(imprensa.complementaryMultiplier),
        keyMessage: Number(imprensa.keyMessage),
        spokespersonMentioned: Number(imprensa.spokespersonMentioned),
        titleMention: Number(imprensa.titleMention),
        pressApproachData: Object.fromEntries(imprensa.pressApproachData.map((i) => [i.id, Number(i.value)])) as any,
        pressVehicleData: Object.fromEntries(imprensa.pressVehicleData.map((i) => [i.id, Number(i.value)])) as any,
        mediaTypeData: {
          printedAndOnline: {
            format: Object.fromEntries(
              imprensa.mediaTypeData
                .filter((m) => m.id === 'Impressos e Online')
                .map((m) => [m.label.toUpperCase(), Number(m.value)]),
            ) as any,
          },
          tv: {
            format: Object.fromEntries(
              imprensa.mediaTypeData.filter((m) => m.id === 'TV').map((m) => [m.label.toUpperCase(), Number(m.value)]),
            ) as any,
          },
          radio: {
            format: Object.fromEntries(
              imprensa.mediaTypeData
                .filter((m) => m.id === 'Rádio')
                .map((m) => [m.label.toUpperCase(), Number(m.value)]),
            ) as any,
          },
        },
      },
    },
    favorability: {
      positive: Number(favorabilidade.positive),
      favorable: Number(favorabilidade.favorable),
      negative: Number(favorabilidade.negative),
      unfavorable: Number(favorabilidade.unfavorable),
    },
  }
}

export const mapResponseToContext = (response: GetCenaResponse, data: CenaData) => {
  const { rede, imprensa } = data
  const { social, press, favorability } = response

  const networkApproachData = rede.networkApproachData.map((item) => ({
    ...item,
    value: String(social.data.socialApproachData?.[item.id]),
  }))

  const networkVehicleData = rede.networkVehicleData.map((item) => ({
    ...item,
    value: String(social.data.socialVehicleData?.[item.id]),
  }))

  const pressApproachData = imprensa.pressApproachData.map((item) => ({
    ...item,
    value: String(press.data.pressApproachData?.[item.id]),
  }))

  const pressVehicleData = imprensa.pressVehicleData.map((item) => ({
    ...item,
    value: String(press.data.pressVehicleData?.[item.id]),
  }))

  const mediaTypeData = imprensa.mediaTypeData.map((item) => {
    const labelUpper = item.label.toUpperCase()

    const categoryMap: Record<string, any> = {
      'Impressos e Online': press.data.mediaTypeData.printedAndOnline,
      TV: press.data.mediaTypeData.tv,
      Rádio: press.data.mediaTypeData.radio,
    }

    const value = categoryMap[item.id]?.format?.[labelUpper] ?? 0

    return { ...item, value: String(value) }
  })

  const multipliersTable = rede.ultraPerformanceData.map((net) => {
    const netValues = social.data.ultraPerformance.multipliers[net.id] || {}

    return {
      ...net,
      interactions: net.interactions.map((inter) => {
        const backendKey = interactionKeyMap[inter.id] || inter.id

        const value = netValues[backendKey] ?? 0

        return { ...inter, value: String(value) }
      }),
    }
  })

  return {
    social: {
      interactionsWeight: String(social.data.interactionsWeight),
      likesP95: String(social.data.likesP95),
      likesP95P99: String(social.data.likesP95P99),
      likesP99: String(social.data.likesP99),
      commentsP95: String(social.data.commentsP95),
      commentsP95P99: String(social.data.commentsP95P99),
      commentsP99: String(social.data.commentsP99),
      approachMultiplier: String(social.data.approachMultiplier),
      channelMultiplier: String(social.data.channelMultiplier),
      complementaryMultiplier: String(social.data.complementaryMultiplier),
      keyMessage: String(social.data.keyMessage),
      extraPointP95: String(social.data.extraPointP95),
      networkApproachData,
      networkVehicleData,
      filterLikesFB: String(social.data.ultraPerformance.filterLikesMeta),
      filterCommentsFB: String(social.data.ultraPerformance.filterCommentsMeta),
      filterSharesFB: String(social.data.ultraPerformance.filterSharesMeta),
      filterLikesYT: String(social.data.ultraPerformance.filterLikesYoutube),
      filterCommentsYT: String(social.data.ultraPerformance.filterCommentsYoutube),
      filterLikesTT: String(social.data.ultraPerformance.filterLikesTiktok),
      filterCommentsTT: String(social.data.ultraPerformance.filterCommentsTiktok),
      filterSharesTT: String(social.data.ultraPerformance.filterSharesTiktok),
      ultraPerformanceData: multipliersTable,
    },
    press: {
      formatWeight: String(press.data.formatWeight),
      approachMultiplier: String(press.data.approachMultiplier),
      vehicleMultiplier: String(press.data.vehicleMultiplier),
      complementaryMultiplier: String(press.data.complementaryMultiplier),
      keyMessage: String(press.data.keyMessage),
      spokespersonMentioned: String(press.data.spokespersonMentioned),
      titleMention: String(press.data.titleMention),
      pressApproachData,
      pressVehicleData,
      mediaTypeData,
    },
    favorability: {
      positive: String(favorability.positive),
      favorable: String(favorability.favorable),
      negative: String(favorability.negative),
      unfavorable: String(favorability.unfavorable),
    },
  }
}
